/**
 * Card drag motion. Values measured from Trello's board: neighbor reflow
 * 100ms and drop landing 150ms, both cubic-bezier(.4, 1, .6, 1);
 * all motion off under prefers-reduced-motion. The held card is not tilted.
 */

export const KANBAN_DRAG_EASING = 'cubic-bezier(0.4, 1, 0.6, 1)';
export const KANBAN_REFLOW_MS = 100;
export const KANBAN_DROP_LANDING_MS = 150;
/** Max time a dropped card holds its drop slot while the consumer updates items. */
export const KANBAN_SETTLE_TIMEOUT_MS = 800;

export const KANBAN_FLIP_ANIMATION_ID = 'kanban-flip';
export const KANBAN_LANDING_ANIMATION_ID = 'kanban-landing';

/** Flip key prefix for the drop placeholder (suffixed with its droppable id). */
export const KANBAN_PLACEHOLDER_FLIP_PREFIX = 'placeholder:';

export interface KanbanFlipPosition {
  /** Scroll container id; positions are only comparable within one scope. */
  scope: string;
  x: number;
  y: number;
  /** 0 = collapsed (drag source moved elsewhere); not a useful start point. */
  height?: number;
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );
}

/** Offset from the new layout position back to the old one; null = no motion. */
export function getFlipDelta(
  previous: KanbanFlipPosition,
  next: KanbanFlipPosition
): { dx: number; dy: number } | null {
  if (previous.scope !== next.scope) return null;
  const dx = previous.x - next.x;
  const dy = previous.y - next.y;
  if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return null;
  return { dx, dy };
}

/** Held-card position → resting slot (Trello's drop landing, without tilt). */
export function getLandingKeyframes(
  from: { left: number; top: number },
  to: { left: number; top: number }
): Keyframe[] {
  const dx = from.left - to.left;
  const dy = from.top - to.top;
  return [
    { transform: `translate3d(${dx}px, ${dy}px, 0)` },
    { transform: 'translate3d(0, 0, 0)' },
  ];
}
